'use client';

import * as React from 'react';

import { useLocale } from 'next-intl';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { locales, usePathname } from '@/i18n/routing';
import { ny } from '@/lib/utils';

interface Language {
    code: string;
    name: string;
}

const languages: Language[] = [
    { code: 'kz', name: 'Қазақ' },
    { code: 'ru', name: 'Русский' },
    { code: 'en', name: 'English' },
];

export default function LanguageSwitcher({
    className,
    align = 'end',
}: {
    className?: string;
    align?: 'center' | 'start' | 'end';
}) {
    const currentPathname = usePathname();
    const currentLocale = useLocale() as (typeof locales)[number];
    const router = useRouter();

    const handleLangChange = (lang: string) => {
        router.replace(`/${lang}${currentPathname}`, { scroll: false });
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className={ny('m-0 flex items-center gap-0 border-0 !bg-transparent p-0 uppercase', className)}
                >
                    {currentLocale}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={align} className="w-[120px]">
                {languages.map((lang) => (
                    <DropdownMenuItem
                        key={lang.code}
                        onClick={() => handleLangChange(lang.code)}
                        className={ny('cursor-pointer', currentLocale === lang.code && 'bg-muted')}
                    >
                        {lang.name}{' '}
                        <Link href={`/${lang.code}${currentPathname}`} hrefLang={lang.code} className="sr-only">
                            {lang.name}
                        </Link>
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
